import { useEffect, useState } from "react";
import {
  Breadcrumbs,
  Grid,
  Link,
  List,
  ListItem,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  AgentState,
  LoadAgentQuery,
  TestIamActionsQuery,
  useDeleteAgentsMutation,
  useLoadAgentQuery,
} from "~/operations";
import { DeleteConfirmDialog } from "~/components/delete-confirm-dialog";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { Home as HomeIcon } from "@mui/icons-material";
import { useCanDeleteAgents } from "~/lib/iam/actions/mondoo/agents/AgentManager/DeleteAgents";
import { FormatRelativeDate, isBefore, sub } from "~/lib/date";
import { Card, CardDetailsGrid, CardHeader } from "~/components/card";
import { DeleteIcon } from "~/components/actions-row";
import { AgentStatusTag, StatusTag } from "~/components/score-card";
import { B, Right, Small, VerticalSpacer } from "~/components/typography";
import { platformTitle } from "~/lib/platform";
import { CardViewLabels } from "~/components/label";
import { Action, ActionMenu } from "~/components/action-menu";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { Config } from "~/constants/configuration_provider";

type Agent = NonNullable<LoadAgentQuery["agent"]>;

type AgentPageState = {
  openEditAgentDialog: boolean;
  openDeleteConfirmDialog: boolean;
  isDeleting: boolean;
  latestAgentRelease: string;
};

export type IntegrationManagedAgentPageProps = {
  scope: SpaceScope | OrganizationScope;
};

export function IntegrationManagedAgentPage({
  scope,
}: IntegrationManagedAgentPageProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { integrationId } = useParams();
  const { isFeatureEnabled } = useFeatureFlags();
  const [state, setState] = useState<AgentPageState>({
    openEditAgentDialog: false,
    openDeleteConfirmDialog: false,
    isDeleting: false,
    latestAgentRelease: "0.0.0",
  });

  const mergeState = (params: Partial<AgentPageState>) => {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  };

  const fetchLatestRelease = async (): Promise<string> => {
    // TODO: should this move someplace more central?
    return fetch(`https://${Config.VITE_RELEASES_HOST}/mondoo/latest.json`)
      .then((res) => res.json())
      .then((releases) => {
        return releases?.version || "0.0.0";
      });
  };

  useEffect(() => {
    (async () => {
      const latestAgentRelease = await fetchLatestRelease();
      mergeState({ latestAgentRelease });
    })();
  }, []);

  const agentMrn = `//agents.api.mondoo.app/spaces/${scope.id}/agents/${integrationId}`;

  const hasDeleteAgentsPermission = useCanDeleteAgents();

  const [deleteAgents] = useDeleteAgentsMutation();

  const { loading, error, data } = useLoadAgentQuery({
    variables: { mrn: agentMrn },
    notifyOnNetworkStatusChange: true,
  });

  setDocumentTitle([
    data?.agent?.name,
    "Managed Clients",
    "Integrations",
    scope.name,
  ]);

  if (loading) return <LoadingPage what="Client" />;

  if (error || !data?.agent) return <LoadingFailedPage what="Client" />;

  const agent = data.agent;

  const handleDeleteAgent = () => {
    mergeState({ openDeleteConfirmDialog: true });
  };

  const handleDeleteAgentConfirm = () => {
    mergeState({ isDeleting: true });

    const agentMrns = [agent].map((x) => x.mrn);
    deleteAgents({
      variables: {
        input: { spaceMrn: scope.mrn, agentMrns: agentMrns },
      },
      update(cache) {
        mergeState({ isDeleting: false });
        enqueueSnackbar("Successfully deleted client", {
          variant: "success",
        });
        cache.evict({ id: "ROOT_QUERY", fieldName: "agents" });
        navigate(`/space/integrations/managed?${scope.params}`);
      },
    }).catch((error) => {
      mergeState({ isDeleting: false });
      enqueueSnackbar("Failed to delete client", {
        variant: "error",
      });
    });
  };

  const handleDeleteAgentClose = () => {
    mergeState({ openDeleteConfirmDialog: false });
  };

  const agentName = agent?.hostname || agent?.name || "Agent";

  // Action are a list of things a user can accomplish though a dropdown menu
  const actions: Action[] = [
    hasDeleteAgentsPermission
      ? {
          label: "Delete",
          action: handleDeleteAgent,
          icon: <DeleteIcon />,
        }
      : undefined,
  ].flatMap((a) => a ?? []);

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/managed"
      component={RouterLink}
      to={`/space/integrations/managed?${scope.params}`}
    >
      Managed Clients
    </Link>,
    <Typography key={1} sx={{ textTransform: "capitalize" }}>
      {agentName}
    </Typography>,
  ];

  return (
    <>
      <DeleteConfirmDialog
        isDeleting={state.isDeleting}
        open={state.openDeleteConfirmDialog}
        items={[agent]}
        onConfirm={handleDeleteAgentConfirm}
        onClose={handleDeleteAgentClose}
        itemName={(item: Agent): string => item.hostname}
        itemType="agent"
      />
      <Breadcrumbs sx={{ mb: 3 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          {AgentCard(agent, actions, state.latestAgentRelease)}
        </Grid>
        {hasAgentErrors(agent) && (
          <Grid
            size={{
              xs: 12,
              sm: 12,
            }}
          >
            {AgentErrorsCard(agent)}
          </Grid>
        )}
        {isFeatureEnabled("Agent Info") && (
          <>
            {agent.serviceAccounts && agent.serviceAccounts.length > 0 && (
              <Grid size={{ xs: 12, sm: 12 }}>
                <Card style={{ width: "100%", cursor: "default" }}>
                  <CardHeader>Connected Service Accounts</CardHeader>
                  <List>
                    {agent.serviceAccounts.map((sa) => (
                      <ListItem key={sa.mrn}>
                        <Link
                          component={RouterLink}
                          to={`/space/settings/serviceaccounts?${scope.params}&queryTerms=${sa.name}`}
                        >
                          {sa.name}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
            )}
            {agent.assets && agent.assets.length > 0 && (
              <Grid size={{ xs: 12, sm: 12 }}>
                <Card style={{ width: "100%", cursor: "default" }}>
                  <CardHeader>Connected Assets</CardHeader>
                  <List>
                    {agent.assets.map((asset) => {
                      const assetId = asset.mrn.split("/").pop() || "";
                      if (!assetId) return null;
                      return (
                        <ListItem key={asset.mrn}>
                          <Link
                            component={RouterLink}
                            to={`/space/inventory/${assetId}?${scope.params}`}
                          >
                            {asset.name}
                          </Link>
                        </ListItem>
                      );
                    })}
                  </List>
                </Card>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  );
}

export const DateString = (timestamp: string) => {
  if (
    timestamp == null ||
    timestamp == "" ||
    timestamp === "0001-01-01T00:00:00Z"
  ) {
    return "Never";
  }
  return FormatRelativeDate(timestamp);
};

const createdWithin24h = (timestamp: string) => {
  if (timestamp == null || timestamp == "") {
    return false;
  }

  return isBefore(sub(new Date(), { days: 1 }), new Date(timestamp));
};

const FloatRight = styled("div")`
  float: right;
`;

const AgentCard = (
  item: Agent,
  actions: Action[],
  latestAgentRelease: string,
) => {
  const id = item.id;
  const lastCheckin = item.status?.lastCheckin
    ? DateString(item.status.lastCheckin)
    : "";

  const statusState = item.status?.state || AgentState.Missing;
  const statusVersion = item.status?.version || "";

  // check if the credentials have been used (missing and no checkedin) and are not created within the last 24h
  const considerDeletion =
    lastCheckin === "" &&
    statusState === AgentState.Missing &&
    !createdWithin24h(item.createdAt);

  const { hostname, ipaddress } = item;
  const subtitle = `${hostname} / ${ipaddress}`;

  const outdated =
    statusVersion !== latestAgentRelease && statusVersion !== "unstable";

  return (
    <Card style={{ width: "100%", minHeight: "345px", cursor: "default" }}>
      <CardHeader style={{ minHeight: "100px" }}>
        <FloatRight>
          <ActionMenu id={`${item.id}-menu`} actions={actions} />
        </FloatRight>
        {item.name}
        <br />
        <Small>{subtitle}</Small>
        <VerticalSpacer space="0.7em" />
        {item.status?.state && <AgentStatusTag status={item.status.state} />}
        {considerDeletion && (
          <>
            <StatusTag status="critical" text="unused" displaydot />
          </>
        )}
        {outdated && item.status?.state === "ACTIVE" && (
          <StatusTag status="critical" text="update required" displaydot />
        )}
        {!outdated && <StatusTag status="good" text="up-to-date" displaydot />}
      </CardHeader>
      <CardDetailsGrid>
        <span>ID</span>
        <Right>
          <B>{id}</B>
        </Right>
        <span>Last Check-in</span>
        <Right>
          <B>{lastCheckin}</B>
        </Right>
        <span>Platform</span>
        <Right>
          <B>{platformTitle(item.platform)}</B>
        </Right>
        <span>Hostname</span>
        <Right>
          <B>{item.hostname}</B>
        </Right>
        <span>IP Address</span>
        <Right>
          <B>{item.ipaddress}</B>
        </Right>
        <span>Client Version</span>
        <Right>
          <B>{item.status?.version}</B>
        </Right>
        <CardViewLabels labels={item.labels} />
      </CardDetailsGrid>
    </Card>
  );
};

const AgentErrorsCard = (item: Agent) => {
  return (
    <Card style={{ width: "100%", cursor: "default" }}>
      <CardHeader>Agent Errors</CardHeader>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Message</TableCell>
            <TableCell align="right">Timestamp</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {item.errors?.map((row) => (
            <TableRow>
              <TableCell component="th" scope="row">
                {row.message}
              </TableCell>
              <TableCell align="right">{row.timestamp}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const hasAgentErrors = (item: Agent) => {
  return item.errors && item.errors.length > 0;
};
